/**
 * ProfileCompletionModal
 *
 * A dismissible, full-screen notice shown after login while a user's profile is
 * still missing mandatory information. It lists the outstanding sections with
 * deep-links and explains, in one friendly sentence, that the profile can't go
 * live / be booked until those are complete.
 *
 * It is a NOTICE, not a hard block — the actual gating (Browse visibility,
 * booking eligibility) is enforced elsewhere. The user can close it; it reappears
 * on the next login (a per-session dismissal flag) until the profile is complete.
 *
 * Talent are nudged until they are BOOKABLE (Tier 3): Level 1 sections make the
 * profile go live, Level 2 (Pricing + Compliance) make them bookable.
 * Organisers are nudged until their profile is activated.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import { getProfileActivationDetails } from '../../utils/profileActivation';
import debug from '../../utils/debug';

// Friendly section labels (mirror ProfileStatusBanner).
const SECTION_LABELS = {
  talentBlueprint: 'Profile Basics (incl. your real name)',
  talentStudio: 'Talent Studio (photo & bio)',
  performanceHub: 'Performance Hub (event types & areas)',
  pricingPortfolio: 'Pricing & Portfolio',
  complianceVault: 'Compliance Vault',
  organiserBlueprint: 'Organiser Profile (incl. your real name)',
};

// Deep-link routes per section (mirror ProfileStatusBanner).
const sectionPath = (section, userId) => ({
  talentBlueprint: `/talent-blueprint/${userId}`,
  talentStudio: `/talent-studio/${userId}`,
  performanceHub: `/performance-hub/${userId}`,
  pricingPortfolio: `/pricing-portfolio/${userId}`,
  complianceVault: `/compliance-vault/${userId}`,
  organiserBlueprint: `/organiser-blueprint/${userId}`,
}[section] || '#');

const ProfileCompletionModal = () => {
  const { currentUser, userType, isVerified } = useAuth();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [open, setOpen] = useState(false);

  const uid = currentUser?.uid;
  const dismissKey = uid ? `profileGateDismissed:${uid}` : null;

  useEffect(() => {
    let cancelled = false;
    // Only evaluate for a verified, typed, logged-in user. Skip if already
    // dismissed this session (reappears on next login — sessionStorage clears).
    if (!uid || !userType || !isVerified) return;
    if (dismissKey && sessionStorage.getItem(dismissKey) === '1') return;

    (async () => {
      try {
        const d = await getProfileActivationDetails(uid, userType);
        if (cancelled || !d) return;
        // Talent: nudge until BOOKABLE (covers Level 1 + Level 2).
        // Organiser: nudge until activated.
        const incomplete = userType === 'Talent' ? !d.bookingReady : !d.isActivated;
        if (incomplete) {
          setDetails(d);
          setOpen(true);
        }
      } catch (err) {
        debug.error('ProfileCompletionModal', 'Failed to load activation details', err);
      }
    })();

    return () => { cancelled = true; };
  }, [uid, userType, isVerified, dismissKey]);

  if (!open || !details) return null;

  const dismiss = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, '1');
    setOpen(false);
  };

  const go = (section) => {
    dismiss();
    navigate(sectionPath(section, uid));
  };

  // Build the outstanding lists.
  const isTalent = userType === 'Talent';
  const level1Missing = (details.missingFields || []);
  const level2Missing = isTalent ? (details.bookingMissingFields || []) : [];

  const Item = ({ section }) => (
    <button
      type="button"
      onClick={() => go(section)}
      style={styles.item}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eef6ff'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f7fafc'; }}
    >
      <span>{SECTION_LABELS[section] || section}</span>
      <span style={styles.itemArrow}>Complete →</span>
    </button>
  );

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <div style={styles.card}>
        <button type="button" onClick={dismiss} style={styles.close} aria-label="Close">×</button>

        <h2 style={styles.heading}>Finish setting up your profile</h2>
        <p style={styles.message}>
          Welcome back! A few required details are still missing. Until they're complete,
          {isTalent
            ? ' your profile can’t go live and you can’t be booked.'
            : ' you can’t view profiles or make bookings.'}
          {' '}It only takes a few minutes — you can close this and finish anytime.
        </p>

        {level1Missing.length > 0 && (
          <div style={styles.group}>
            <h3 style={styles.groupTitle}>
              {isTalent ? 'Required to make your profile live:' : 'Required to start booking talent:'}
            </h3>
            {level1Missing.map((s) => <Item key={s} section={s} />)}
          </div>
        )}

        {isTalent && level2Missing.length > 0 && (
          <div style={styles.group}>
            <h3 style={styles.groupTitle}>Required to become bookable:</h3>
            {level2Missing.map((s) => <Item key={s} section={s} />)}
          </div>
        )}

        <button type="button" onClick={dismiss} style={styles.laterBtn}>
          I&apos;ll finish later
        </button>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'linear-gradient(135deg, rgba(0,34,68,0.92) 0%, rgba(20,72,116,0.92) 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1rem', fontFamily: 'Montserrat, sans-serif', zIndex: 2000,
  },
  card: {
    backgroundColor: '#fff', borderRadius: '20px', padding: '2.5rem',
    width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
  },
  close: {
    position: 'absolute', top: '1rem', right: '1.25rem', border: 'none',
    background: 'none', fontSize: '1.75rem', lineHeight: 1, color: '#94a3b8',
    cursor: 'pointer',
  },
  heading: { margin: '0 0 0.75rem', color: '#144874', fontSize: '1.5rem', fontWeight: 700 },
  message: { margin: '0 0 1.5rem', color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 },
  group: { marginBottom: '1.25rem' },
  groupTitle: { margin: '0 0 0.5rem', color: '#0f172a', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' },
  item: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', padding: '0.85rem 1rem', marginBottom: '0.5rem',
    backgroundColor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '12px',
    cursor: 'pointer', fontSize: '0.95rem', color: '#1e293b', fontFamily: 'inherit',
    transition: 'background-color 0.15s ease',
  },
  itemArrow: { color: '#01a2f1', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' },
  laterBtn: {
    marginTop: '0.5rem', width: '100%', padding: '0.75rem', border: 'none',
    background: 'none', color: '#64748b', fontSize: '0.9rem', cursor: 'pointer',
    fontFamily: 'inherit', textDecoration: 'underline',
  },
};

export default ProfileCompletionModal;
